import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import * as clientsApi from '../../api/clients';
import { Client } from '../../types/client';
import { useSnackbar } from 'notistack';

interface IssueLOAButtonProps {
  client: Client;
  onLOAIssued: () => void;
}

const IssueLOAButton: React.FC<IssueLOAButtonProps> = ({ client, onLOAIssued }) => {
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const canIssueLOA = client.properties && client.properties.length > 0;

  const handleIssueLOA = async () => {
    try {
      setLoading(true);
      
      // Generate and send LOA
      await clientsApi.issueLOA(client._id);
      
      enqueueSnackbar('Letter of Authority has been issued successfully', { variant: 'success' });
      onLOAIssued();
      setShowConfirmDialog(false);
    } catch (error: any) {
      
      enqueueSnackbar(error.response?.data?.message || 'Failed to issue Letter of Authority', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!canIssueLOA) {
    return (
      <Button
        variant="contained"
        disabled
        title="Add at least one property to issue LOA"
      >
        Issue LOA
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setShowConfirmDialog(true)}
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : 'Issue LOA'}
      </Button>

      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(25, 25, 25, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            color: 'white',
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>Issue Letter of Authority</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            This will generate a Letter of Authority for {client.companyName} and send it via email.
            The client will be able to review and accept/decline the LOA through a secure link.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={() => setShowConfirmDialog(false)}
            sx={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleIssueLOA} 
            variant="contained" 
            color="primary"
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            Issue LOA
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IssueLOAButton;
